import React, { useMemo, useState } from "react";
import { Drawer, Tag, Tooltip, Progress } from "antd";
import {
  DollarOutlined,
  TrophyOutlined,
  WarningOutlined,
  ThunderboltOutlined,
  ClockCircleOutlined,
  RiseOutlined,
  FallOutlined,
  StarOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  PieChartOutlined,
  AimOutlined,
  DownOutlined,
  UpOutlined,
} from "@ant-design/icons";
import {
  colors,
  getSupplierInitials,
  formatMYR,
  getSupplierGrandTotal,
  calculateSummaryData,
  MaterialInfo,
} from "./comparisonUtils";

interface InsightsDrawerProps {
  visible: boolean;
  onClose: () => void;
  suppliers: any[];
  evaluationScoreMap: Record<string, any>;
  materialNames: MaterialInfo[];
  shortlistedData: any[];
}

const InsightsDrawer: React.FC<InsightsDrawerProps> = ({
  visible,
  onClose,
  suppliers,
  evaluationScoreMap,
  materialNames,
  shortlistedData,
}) => {
  // --- Compute all insights from data ---

  const summaryData = useMemo(
    () => calculateSummaryData(suppliers, materialNames),
    [suppliers, materialNames]
  );

  // Grand totals per supplier
  const supplierTotals = useMemo(() => {
    return suppliers.map((s, idx) => {
      const grandTotal = getSupplierGrandTotal(s, materialNames);
      const evalData = evaluationScoreMap[s.supplierID];
      return {
        supplierID: s.supplierID,
        supplierName: s.supplierName,
        grandTotal,
        rank: evalData?.rank ?? 999,
        score: evalData?.overallScore ?? 0,
        paymentTerms: s.paymentTerms,
        colorIndex: idx,
      };
    });
  }, [suppliers, evaluationScoreMap, materialNames]);

  const sortedByPrice = useMemo(
    () => [...supplierTotals].sort((a, b) => a.grandTotal - b.grandTotal),
    [supplierTotals]
  );

  const lowestPriced = sortedByPrice[0];
  const mostExpensive = sortedByPrice[sortedByPrice.length - 1];
  const totalSavings =
    lowestPriced && mostExpensive
      ? mostExpensive.grandTotal - lowestPriced.grandTotal
      : 0;
  const savingsPct =
    mostExpensive && mostExpensive.grandTotal > 0
      ? (totalSavings / mostExpensive.grandTotal) * 100
      : 0;

  // Per-item analysis (works for both materials and work lines)
  const itemInsights = useMemo(() => {
    return materialNames.map((mat) => {
      const entries = suppliers
        .map((s, idx) => {
          const li = (s.lineItems || []).find(
            (l: any) => l.tenderMaterialId === mat.tenderMaterialId
          );
          if (!li) return null;
          return {
            supplierID: s.supplierID,
            supplierName: s.supplierName,
            unitPrice: li.quotedUnitPrice,
            totalPrice: li.quotedQuantity * li.quotedUnitPrice + (li.totalInstallationCharges || 0) - (li.discountAmount || 0),
            supplyQty: li.quotedQuantity,
            askedQty: li.askedQuantity ?? null,
            colorIndex: idx,
          };
        })
        .filter(Boolean) as any[];

      const sorted = [...entries].sort(
        (a, b) => a.totalPrice - b.totalPrice
      );
      const minPrice = sorted[0]?.totalPrice ?? 0;
      const maxPrice = sorted[sorted.length - 1]?.totalPrice ?? 0;
      const spread = maxPrice - minPrice;
      const spreadPct = maxPrice > 0 ? (spread / maxPrice) * 100 : 0;
      const underQuoted = entries.filter(
        (e) => e.askedQty != null && e.supplyQty < e.askedQty
      );

      return {
        itemName: mat.materialName,
        usage: mat.usage || "",
        tenderMaterialId: mat.tenderMaterialId,
        entries,
        sorted,
        lowestPriced: sorted[0],
        mostExpensive: sorted[sorted.length - 1],
        spread,
        spreadPct,
        underQuoted,
        supplierCount: entries.length,
      };
    });
  }, [materialNames, suppliers]);

  // Item with biggest price spread
  const biggestSpreadItem = useMemo(() => {
    return [...itemInsights].sort(
      (a, b) => b.spreadPct - a.spreadPct
    )[0];
  }, [itemInsights]);

  // Item with tightest pricing
  const tightestItem = useMemo(() => {
    return [...itemInsights]
      .filter((m) => m.entries.length > 1)
      .sort((a, b) => a.spreadPct - b.spreadPct)[0];
  }, [itemInsights]);

  // Best value supplier (60% price, 40% score)
  const bestValueSupplier = useMemo(() => {
    if (supplierTotals.length === 0) return null;
    const maxTotal = Math.max(...supplierTotals.map((s) => s.grandTotal));
    return [...supplierTotals].sort((a, b) => {
      const priceScoreA =
        maxTotal > 0
          ? ((maxTotal - a.grandTotal) / maxTotal) * 100
          : 0;
      const priceScoreB =
        maxTotal > 0
          ? ((maxTotal - b.grandTotal) / maxTotal) * 100
          : 0;
      const valA = priceScoreA * 0.6 + a.score * 0.4;
      const valB = priceScoreB * 0.6 + b.score * 0.4;
      return valB - valA;
    })[0];
  }, [supplierTotals]);

  // Highest scored supplier
  const topScorer = useMemo(
    () => [...supplierTotals].sort((a, b) => b.score - a.score)[0],
    [supplierTotals]
  );

  // Supplier competitiveness: how many items each supplier has lowest price / most expensive on
  const supplierCompetitiveness = useMemo(() => {
    const map: Record<
      string,
      {
        supplierName: string;
        lowestCount: number;
        expensiveCount: number;
        quotedCount: number;
        colorIndex: number;
      }
    > = {};
    suppliers.forEach((s, idx) => {
      map[s.supplierID] = {
        supplierName: s.supplierName,
        lowestCount: 0,
        expensiveCount: 0,
        quotedCount: 0,
        colorIndex: idx,
      };
    });
    itemInsights.forEach((item) => {
      if (item.sorted.length === 0) return;
      item.entries.forEach((e) => {
        if (map[e.supplierID]) map[e.supplierID].quotedCount++;
      });
      if (item.lowestPriced) {
        map[item.lowestPriced.supplierID].lowestCount++;
      }
      if (item.sorted.length > 1 && item.mostExpensive) {
        map[item.mostExpensive.supplierID].expensiveCount++;
      }
    });
    return Object.entries(map)
      .map(([id, data]) => ({ supplierID: id, ...data }))
      .sort((a, b) => b.lowestCount - a.lowestCount);
  }, [suppliers, itemInsights]);

  // Per-supplier material price breakdown (lowest to highest)
  const supplierMaterialBreakdown = useMemo(() => {
    const map: Record<string, { materialName: string; usage: string; totalPrice: number; isLowest: boolean; isHighest: boolean }[]> = {};
    suppliers.forEach((s) => {
      const materials: { materialName: string; usage: string; totalPrice: number; isLowest: boolean; isHighest: boolean }[] = [];
      itemInsights.forEach((item) => {
        const entry = item.entries.find((e: any) => e.supplierID === s.supplierID);
        if (!entry) return;
        materials.push({
          materialName: item.itemName,
          usage: item.usage || "",
          totalPrice: entry.totalPrice,
          isLowest: item.lowestPriced?.supplierID === s.supplierID,
          isHighest: item.sorted.length > 1 && item.mostExpensive?.supplierID === s.supplierID,
        });
      });
      materials.sort((a, b) => {
        // Lowest-tagged first, then highest-tagged
        if (a.isLowest && !b.isLowest) return -1;
        if (!a.isLowest && b.isLowest) return 1;
        return a.totalPrice - b.totalPrice;
      });
      map[s.supplierID] = materials;
    });
    return map;
  }, [suppliers, itemInsights]);

  const [expandedCompSupplier, setExpandedCompSupplier] = useState<string | null>(null);

  // Scope coverage: suppliers who didn't quote on all items
  const totalItems = materialNames.length;
  const partialCoverageSuppliers = useMemo(() => {
    return supplierCompetitiveness.filter(
      (s) => s.quotedCount < totalItems
    );
  }, [supplierCompetitiveness, totalItems]);

  // Payment terms grouped
  const paymentTermsMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    suppliers.forEach((s) => {
      const term = s.paymentTerms || "N/A";
      if (!map[term]) map[term] = [];
      map[term].push(s.supplierName);
    });
    return map;
  }, [suppliers]);

  // Shortlist progress
  const totalAskedQty = useMemo(() => {
    let total = 0;
    materialNames.forEach((mat) => {
      const s = suppliers[0];
      const li = (s?.lineItems || []).find(
        (l: any) => l.tenderMaterialId === mat.tenderMaterialId
      );
      total += li?.askedQuantity ?? 0;
    });
    return total;
  }, [materialNames, suppliers]);

  const totalAwardedQty = useMemo(() => {
    return shortlistedData.reduce((sum, s) => sum + (s.awardQty || 0), 0);
  }, [shortlistedData]);

  // Under-quoted items (supplier quoting less than asked) — only when askedQty exists
  const allUnderQuoted = useMemo(
    () => itemInsights.filter((m) => m.underQuoted.length > 0),
    [itemInsights]
  );

  // Delivery insights — only rendered when schedule data exists
  const deliveryInsights = useMemo(() => {
    const results: {
      supplierName: string;
      avgLeadDays: number;
      earliestETA: string;
    }[] = [];
    suppliers.forEach((s) => {
      let totalLead = 0;
      let count = 0;
      let earliestETA = "";
      (s.lineItems || []).forEach((li: any) => {
        if (li.planningSchedule?.leadDays) {
          totalLead += li.planningSchedule.leadDays;
          count++;
        }
        if (li.shipmentSchedule?.eta) {
          if (!earliestETA || li.shipmentSchedule.eta < earliestETA) {
            earliestETA = li.shipmentSchedule.eta;
          }
        }
      });
      if (count > 0) {
        results.push({
          supplierName: s.supplierName,
          avgLeadDays: totalLead / count,
          earliestETA,
        });
      }
    });
    return results.sort((a, b) => a.avgLeadDays - b.avgLeadDays);
  }, [suppliers]);

  // --- Render helpers ---

  const sectionStyle: React.CSSProperties = {
    marginBottom: 20,
    padding: "14px 16px",
    background: "#fafafa",
    borderRadius: 10,
    border: "1px solid #f0f0f0",
  };

  const sectionHeaderStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 10,
    fontWeight: 600,
    fontSize: 14,
    color: "#333",
  };

  const statRowStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 0",
    fontSize: 13,
    borderBottom: "1px solid #f5f5f5",
  };

  const ITEM_SPREAD_LIMIT = 5;
  const [showAllItems, setShowAllItems] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const toggleItemExpand = (itemName: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemName)) next.delete(itemName);
      else next.add(itemName);
      return next;
    });
  };
  const visibleItemInsights = showAllItems
    ? itemInsights
    : itemInsights.slice(0, ITEM_SPREAD_LIMIT);
  const hasMoreItems = itemInsights.length > ITEM_SPREAD_LIMIT;

  const noData = suppliers.length === 0;

  // Determine if this is a works-based tender (no askedQuantity on line items)
  const hasAskedQty = suppliers.some((s: any) =>
    (s.lineItems || []).some((li: any) => li.askedQuantity != null)
  );

  return (
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ThunderboltOutlined style={{ color: "#faad14" }} />
          <span>Tender Insights</span>
          <Tag color="blue" style={{ fontSize: 11, marginLeft: 4 }}>
            {suppliers.length} Suppliers &middot; {materialNames.length}{" "}
            Line Items
          </Tag>
        </div>
      }
      width={700}
      onClose={onClose}
      open={visible}
      styles={{ body: { padding: "20px 24px" } }}
    >
      {noData ? (
        <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
          No supplier data available.
        </div>
      ) : (
        <>
          {/* --- 1. Price Overview --- */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <DollarOutlined style={{ color: "#52c41a" }} />
              Price Overview
            </div>
            <div style={statRowStyle}>
              <span style={{ color: "#666" }}>Lowest Grand Total</span>
              <span>
                <b style={{ color: "#15803d" }}>
                  INR {formatMYR(lowestPriced.grandTotal)}
                </b>{" "}
                <Tag
                  color="green"
                  style={{ fontSize: 10, marginLeft: 4 }}
                >
                  {lowestPriced.supplierName}
                </Tag>
              </span>
            </div>
            <div style={statRowStyle}>
              <span style={{ color: "#666" }}>Highest Grand Total</span>
              <span>
                <b style={{ color: "#cf1322" }}>
                  INR {formatMYR(mostExpensive.grandTotal)}
                </b>{" "}
                <Tag
                  color="red"
                  style={{ fontSize: 10, marginLeft: 4 }}
                >
                  {mostExpensive.supplierName}
                </Tag>
              </span>
            </div>
            <div style={statRowStyle}>
              <span style={{ color: "#666" }}>Price Spread</span>
              <span>
                <b>INR {formatMYR(totalSavings)}</b>{" "}
                <span style={{ color: "#888", fontSize: 12 }}>
                  ({savingsPct.toFixed(1)}% difference)
                </span>
              </span>
            </div>
            <div style={{ ...statRowStyle, borderBottom: "none" }}>
              <span style={{ color: "#666" }}>Average Grand Total</span>
              <b>
                INR{" "}
                {formatMYR(
                  supplierTotals.reduce((s, t) => s + t.grandTotal, 0) /
                    supplierTotals.length
                )}
              </b>
            </div>
          </div>

          {/* --- 2. Ranking Summary --- */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <TrophyOutlined style={{ color: "#faad14" }} />
              Ranking Summary
            </div>
            {topScorer && topScorer.score > 0 && (
              <div style={statRowStyle}>
                <span style={{ color: "#666" }}>
                  <StarOutlined
                    style={{ color: "#2563eb", marginRight: 4 }}
                  />
                  Top Evaluated
                </span>
                <span>
                  <b style={{ color: "#1d4ed8" }}>
                    {topScorer.supplierName}
                  </b>{" "}
                  <span style={{ color: "#888", fontSize: 12 }}>
                    Score: {topScorer.score.toFixed(1)}/100
                  </span>
                </span>
              </div>
            )}
            <div style={statRowStyle}>
              <span style={{ color: "#666" }}>
                <DollarOutlined
                  style={{ color: "#52c41a", marginRight: 4 }}
                />
                Lowest Priced Supplier
              </span>
              <span>
                <b style={{ color: "#15803d" }}>
                  {lowestPriced.supplierName}
                </b>{" "}
                <span style={{ color: "#888", fontSize: 12 }}>
                  INR {formatMYR(lowestPriced.grandTotal)}
                </span>
              </span>
            </div>
            {bestValueSupplier && topScorer && topScorer.score > 0 && (
              <div style={{ ...statRowStyle, borderBottom: "none" }}>
                <span style={{ color: "#666" }}>
                  <ThunderboltOutlined
                    style={{ color: "#9333ea", marginRight: 4 }}
                  />
                  Best Value{" "}
                  <span style={{ fontSize: 11, color: "#aaa" }}>
                    (60% price, 40% score)
                  </span>
                </span>
                <b style={{ color: "#7e22ce" }}>
                  {bestValueSupplier.supplierName}
                </b>
              </div>
            )}
            {lowestPriced &&
              topScorer &&
              topScorer.score > 0 &&
              lowestPriced.supplierID !== topScorer.supplierID && (
                <div
                  style={{
                    marginTop: 8,
                    padding: "8px 12px",
                    background: "#fffbe6",
                    border: "1px solid #ffe58f",
                    borderRadius: 6,
                    fontSize: 12,
                    color: "#8c6d1f",
                  }}
                >
                  <WarningOutlined style={{ marginRight: 4 }} />
                  The lowest priced supplier and the top-evaluated supplier are
                  different. Consider the best-value option for a balanced
                  decision.
                </div>
              )}
          </div>

          {/* --- 3. Supplier Competitiveness --- */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <PieChartOutlined style={{ color: "#1890ff" }} />
              Supplier Competitiveness
              <Tooltip
                title="Shows how many line items each supplier has the lowest or highest price on. Helps identify which supplier dominates on pricing."
                placement="right"
                overlayStyle={{ maxWidth: 300 }}
              >
                <InfoCircleOutlined
                  style={{ color: "#bbb", fontSize: 13, cursor: "pointer" }}
                />
              </Tooltip>
            </div>
            {supplierCompetitiveness.map((sc) => {
              const isExpanded = expandedCompSupplier === sc.supplierID;
              const materials = supplierMaterialBreakdown[sc.supplierID] || [];
              return (
                <div key={sc.supplierID}>
                  <div
                    onClick={() => setExpandedCompSupplier(isExpanded ? null : sc.supplierID)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "7px 0",
                      borderBottom: "1px solid #f5f5f5",
                      fontSize: 13,
                      cursor: "pointer",
                      transition: "background 0.2s",
                      borderRadius: 4,
                      paddingLeft: 4,
                      paddingRight: 4,
                      backgroundColor: isExpanded ? "#fafafa" : "transparent",
                    }}
                  >
                    {isExpanded ? (
                      <UpOutlined style={{ fontSize: 10, color: "#999", flexShrink: 0 }} />
                    ) : (
                      <DownOutlined style={{ fontSize: 10, color: "#999", flexShrink: 0 }} />
                    )}
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        backgroundColor: colors[sc.colorIndex % colors.length],
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 10,
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      {getSupplierInitials(sc.supplierName)}
                    </div>
                    <span
                      style={{
                        flex: 1,
                        fontWeight: 500,
                        color: "#333",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {sc.supplierName}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        gap: 6,
                        flexShrink: 0,
                      }}
                    >
                      <Tag
                        color="green"
                        style={{ fontSize: 11, margin: 0 }}
                      >
                        Lowest: {sc.lowestCount}/{totalItems}
                      </Tag>
                      {sc.expensiveCount > 0 && (
                        <Tag
                          color="red"
                          style={{ fontSize: 11, margin: 0 }}
                        >
                          Highest: {sc.expensiveCount}/{totalItems}
                        </Tag>
                      )}
                      {sc.quotedCount < totalItems && (
                        <Tag style={{ fontSize: 11, margin: 0, color: "#999" }}>
                          Quoted: {sc.quotedCount}/{totalItems}
                        </Tag>
                      )}
                    </div>
                  </div>
                  {isExpanded && materials.filter((m) => m.isLowest || m.isHighest).length > 0 && (
                    <div
                      style={{
                        padding: "8px 8px 8px 46px",
                        backgroundColor: "#fafafa",
                        borderBottom: "1px solid #f0f0f0",
                        borderRadius: "0 0 4px 4px",
                      }}
                    >
                      <div style={{ fontSize: 11, color: "#888", marginBottom: 6, fontWeight: 600 }}>
                        Materials (Lowest → Highest)
                      </div>
                      {materials.filter((m) => m.isLowest || m.isHighest).map((mat, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: "4px 0",
                            borderBottom: idx < materials.length - 1 ? "1px solid #f0f0f0" : "none",
                            fontSize: 12,
                          }}
                        >
                          <span style={{ flex: 1, color: "#555", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {idx + 1}. {mat.materialName}
                            {mat.usage && (
                              mat.usage.length > 20 ? (
                                <Tooltip title={mat.usage}>
                                  <span style={{ color: "#999", fontSize: 11, marginLeft: 6, cursor: "pointer" }}>({mat.usage.slice(0, 20)}...)</span>
                                </Tooltip>
                              ) : (
                                <span style={{ color: "#999", fontSize: 11, marginLeft: 6 }}>({mat.usage})</span>
                              )
                            )}
                          </span>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                            <span style={{ fontWeight: 600, color: "#333" }}>
                              INR {formatMYR(mat.totalPrice)}
                            </span>
                            {mat.isLowest && (
                              <Tag color="green" style={{ fontSize: 10, margin: 0, lineHeight: "16px" }}>
                                Lowest
                              </Tag>
                            )}
                            {mat.isHighest && (
                              <Tag color="red" style={{ fontSize: 10, margin: 0, lineHeight: "16px" }}>
                                Highest
                              </Tag>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* --- 4. Item-wise Price Comparison --- */}
          <div style={sectionStyle}>
            <div
              style={{
                ...sectionHeaderStyle,
                justifyContent: "space-between",
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <RiseOutlined style={{ color: "#1890ff" }} />
                Item-wise Price Comparison
                <Tooltip
                  title="Shows how much quoted prices vary across suppliers for each line item. A high spread means suppliers differ significantly on pricing. A low spread indicates consistent market rates."
                  placement="right"
                  overlayStyle={{ maxWidth: 300 }}
                >
                  <InfoCircleOutlined
                    style={{
                      color: "#bbb",
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              </span>
              <Tag style={{ fontSize: 11, margin: 0 }}>
                {itemInsights.length} item
                {itemInsights.length !== 1 ? "s" : ""}
              </Tag>
            </div>
            <div>
              {visibleItemInsights.map((m) => {
                const isExpanded = expandedItems.has(m.itemName);
                return (
                  <div
                    key={m.itemName}
                    style={{
                      marginBottom: 6,
                      border: "1px solid #f0f0f0",
                      borderRadius: 8,
                      background: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "8px 12px",
                        cursor: "pointer",
                      }}
                      onClick={() => toggleItemExpand(m.itemName)}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 12,
                          marginBottom: 2,
                        }}
                      >
                        <span
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          {isExpanded ? (
                            <UpOutlined style={{ fontSize: 10, color: "#999" }} />
                          ) : (
                            <DownOutlined style={{ fontSize: 10, color: "#999" }} />
                          )}
                          <Tooltip title={m.usage && m.usage.length > 20 ? m.usage : m.itemName}>
                            <span
                              style={{
                                fontWeight: 500,
                                color: "#333",
                                maxWidth: 320,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "inline-block",
                              }}
                            >
                              {m.itemName}
                              {m.usage && (
                                m.usage.length > 20 ? (
                                  <span style={{ color: "#999", fontSize: 11, fontWeight: 400, marginLeft: 6 }}>({m.usage.slice(0, 20)}...)</span>
                                ) : (
                                  <span style={{ color: "#999", fontSize: 11, fontWeight: 400, marginLeft: 6 }}>({m.usage})</span>
                                )
                              )}
                            </span>
                          </Tooltip>
                        </span>
                        <span
                          style={{
                            color: "#888",
                            fontSize: 11,
                            flexShrink: 0,
                            marginLeft: 8,
                          }}
                        >
                          {m.supplierCount} supplier{m.supplierCount !== 1 ? "s" : ""} &middot;{" "}
                          {m.spreadPct.toFixed(1)}% spread
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          fontSize: 11,
                        }}
                      >
                        <Tooltip
                          title={`Lowest: ${m.lowestPriced?.supplierName}`}
                        >
                          <span
                            style={{
                              color: "#15803d",
                              minWidth: 100,
                              fontWeight: 500,
                            }}
                          >
                            {formatMYR(m.lowestPriced?.totalPrice ?? 0)}
                          </span>
                        </Tooltip>
                        <Progress
                          percent={m.spreadPct}
                          showInfo={false}
                          strokeColor={
                            m.spreadPct > 10 ? "#faad14" : "#52c41a"
                          }
                          trailColor="#f0f0f0"
                          size="small"
                          style={{ flex: 1 }}
                        />
                        <Tooltip
                          title={`Highest: ${m.mostExpensive?.supplierName}`}
                        >
                          <span
                            style={{
                              color: "#cf1322",
                              minWidth: 100,
                              textAlign: "right",
                              fontWeight: 500,
                            }}
                          >
                            {formatMYR(m.mostExpensive?.totalPrice ?? 0)}
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                    {isExpanded && (
                      <div
                        style={{
                          borderTop: "1px solid #f0f0f0",
                          padding: "6px 12px 10px",
                          background: "#fafcff",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 10,
                            color: "#999",
                            padding: "2px 0 4px",
                            borderBottom: "1px solid #f0f0f0",
                            marginBottom: 4,
                          }}
                        >
                          <span style={{ flex: 2 }}>Supplier</span>
                          <span style={{ flex: 1, textAlign: "right" }}>Unit Price</span>
                          <span style={{ flex: 1, textAlign: "right" }}>Qty</span>
                          <span style={{ flex: 1, textAlign: "right" }}>Total Price</span>
                          <span style={{ width: 50, textAlign: "right" }}>Rank</span>
                        </div>
                        {m.sorted.map((entry: any, idx: number) => {
                          const isLowest = idx === 0;
                          const isHighest = idx === m.sorted.length - 1 && m.sorted.length > 1;
                          const pctFromLowest =
                            m.lowestPriced?.totalPrice > 0
                              ? ((entry.totalPrice - m.lowestPriced.totalPrice) /
                                  m.lowestPriced.totalPrice) *
                                100
                              : 0;
                          return (
                            <div
                              key={entry.supplierID}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                fontSize: 12,
                                padding: "5px 0",
                                borderBottom:
                                  idx < m.sorted.length - 1
                                    ? "1px solid #f5f5f5"
                                    : "none",
                              }}
                            >
                              <span
                                style={{
                                  flex: 2,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    width: 22,
                                    height: 22,
                                    borderRadius: "50%",
                                    backgroundColor:
                                      colors[entry.colorIndex % colors.length],
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 9,
                                    fontWeight: 600,
                                    flexShrink: 0,
                                  }}
                                >
                                  {getSupplierInitials(entry.supplierName)}
                                </div>
                                <Tooltip title={entry.supplierName}>
                                  <span
                                    style={{
                                      fontWeight: isLowest ? 600 : 400,
                                      color: isLowest
                                        ? "#15803d"
                                        : isHighest
                                        ? "#cf1322"
                                        : "#333",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {entry.supplierName}
                                  </span>
                                </Tooltip>
                                {isLowest && (
                                  <Tag
                                    color="green"
                                    style={{ fontSize: 9, margin: 0, lineHeight: "16px" }}
                                  >
                                    Lowest
                                  </Tag>
                                )}
                              </span>
                              <span
                                style={{
                                  flex: 1,
                                  textAlign: "right",
                                  color: "#555",
                                  fontSize: 11,
                                }}
                              >
                                {formatMYR(entry.unitPrice)}
                              </span>
                              <span
                                style={{
                                  flex: 1,
                                  textAlign: "right",
                                  color: "#555",
                                  fontSize: 11,
                                }}
                              >
                                {entry.supplyQty}
                              </span>
                              <span
                                style={{
                                  flex: 1,
                                  textAlign: "right",
                                  fontWeight: 600,
                                  color: isLowest
                                    ? "#15803d"
                                    : isHighest
                                    ? "#cf1322"
                                    : "#333",
                                }}
                              >
                                {formatMYR(entry.totalPrice)}
                              </span>
                              <span
                                style={{
                                  width: 50,
                                  textAlign: "right",
                                  fontSize: 10,
                                  color: pctFromLowest === 0 ? "#15803d" : "#e8890c",
                                }}
                              >
                                {pctFromLowest === 0
                                  ? "Best"
                                  : `+${pctFromLowest.toFixed(1)}%`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {hasMoreItems && (
              <a
                onClick={() => setShowAllItems((prev) => !prev)}
                style={{
                  display: "block",
                  textAlign: "center",
                  fontSize: 12,
                  color: "#1890ff",
                  cursor: "pointer",
                  padding: "6px 0 2px",
                }}
              >
                {showAllItems
                  ? "Show less"
                  : `Show all ${itemInsights.length} items`}
              </a>
            )}
            {biggestSpreadItem &&
              biggestSpreadItem.spreadPct > 5 && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 12px",
                    background: "#fff7e6",
                    border: "1px solid #ffd591",
                    borderRadius: 6,
                    fontSize: 12,
                    color: "#ad6800",
                  }}
                >
                  <RiseOutlined style={{ marginRight: 4 }} />
                  <b>{biggestSpreadItem.itemName}</b> has the widest
                  price variation (
                  {biggestSpreadItem.spreadPct.toFixed(1)}%).
                </div>
              )}
            {tightestItem && tightestItem.spreadPct < 5 && (
              <div
                style={{
                  marginTop: 6,
                  padding: "8px 12px",
                  background: "#f6ffed",
                  border: "1px solid #b7eb8f",
                  borderRadius: 6,
                  fontSize: 12,
                  color: "#389e0d",
                }}
              >
                <CheckCircleOutlined style={{ marginRight: 4 }} />
                <b>{tightestItem.itemName}</b> has the tightest pricing (
                {tightestItem.spreadPct.toFixed(1)}% spread) — consistent
                market rate.
              </div>
            )}
          </div>

          {/* --- 5. Scope Coverage --- */}
          {partialCoverageSuppliers.length > 0 && (
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <AimOutlined style={{ color: "#fa8c16" }} />
                Scope Coverage
                <Tooltip
                  title="Flags suppliers who have not quoted on all line items. Partial coverage may require splitting the award or requesting revised quotes."
                  placement="right"
                  overlayStyle={{ maxWidth: 300 }}
                >
                  <InfoCircleOutlined
                    style={{
                      color: "#bbb",
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              </div>
              <div
                style={{ fontSize: 12, color: "#666", marginBottom: 8 }}
              >
                {partialCoverageSuppliers.length} of {suppliers.length}{" "}
                suppliers did not quote on all {totalItems} line items:
              </div>
              {partialCoverageSuppliers.map((sc) => (
                <div key={sc.supplierID} style={statRowStyle}>
                  <span style={{ color: "#333", fontWeight: 500 }}>
                    {sc.supplierName}
                  </span>
                  <span>
                    <Tag
                      color="orange"
                      style={{ fontSize: 11, margin: 0 }}
                    >
                      {sc.quotedCount} of {totalItems} items quoted
                    </Tag>
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* --- 6. Delivery Comparison (only when schedule data exists) --- */}
          {deliveryInsights.length > 0 && (
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <ClockCircleOutlined style={{ color: "#1890ff" }} />
                Delivery Comparison
              </div>
              {deliveryInsights.map((d, idx) => (
                <div key={d.supplierName} style={statRowStyle}>
                  <span
                    style={{
                      color: idx === 0 ? "#15803d" : "#666",
                      fontWeight: idx === 0 ? 600 : 400,
                    }}
                  >
                    {d.supplierName}
                    {idx === 0 && (
                      <Tag
                        color="green"
                        style={{
                          fontSize: 10,
                          marginLeft: 6,
                          lineHeight: "18px",
                        }}
                      >
                        Fastest
                      </Tag>
                    )}
                  </span>
                  <span style={{ fontSize: 12 }}>
                    <b>{d.avgLeadDays.toFixed(0)}d</b> avg lead
                    {d.earliestETA && (
                      <span style={{ color: "#888", marginLeft: 8 }}>
                        ETA: {d.earliestETA}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* --- 7. Payment Terms --- */}
          <div style={sectionStyle}>
            <div style={sectionHeaderStyle}>
              <ClockCircleOutlined style={{ color: "#fa8c16" }} />
              Payment Terms
            </div>
            {Object.entries(paymentTermsMap).map(
              ([term, supplierNames]) => (
                <div key={term} style={statRowStyle}>
                  <Tag style={{ fontSize: 11 }}>{term}</Tag>
                  <span style={{ fontSize: 12, color: "#555" }}>
                    {supplierNames.join(", ")}
                  </span>
                </div>
              )
            )}
          </div>

          {/* --- 8. Risk Flags --- */}
          {(allUnderQuoted.length > 0 ||
            supplierTotals.some((s) => s.score > 0 && s.score < 75) ||
            partialCoverageSuppliers.length > 0) && (
            <div
              style={{
                ...sectionStyle,
                background: "#fff2f0",
                border: "1px solid #ffccc7",
              }}
            >
              <div style={sectionHeaderStyle}>
                <WarningOutlined style={{ color: "#cf1322" }} />
                Risk Flags
              </div>
              {allUnderQuoted.map((m) => (
                <div
                  key={m.itemName}
                  style={{
                    fontSize: 12,
                    color: "#8c4646",
                    padding: "4px 0",
                  }}
                >
                  <WarningOutlined
                    style={{ marginRight: 4, fontSize: 11 }}
                  />
                  <b>{m.itemName}</b>:{" "}
                  {m.underQuoted
                    .map(
                      (u: any) =>
                        `${u.supplierName} quoted ${u.supplyQty} vs ${u.askedQty} asked`
                    )
                    .join("; ")}
                </div>
              ))}
              {supplierTotals
                .filter((s) => s.score > 0 && s.score < 75)
                .map((s) => (
                  <div
                    key={s.supplierID}
                    style={{
                      fontSize: 12,
                      color: "#8c4646",
                      padding: "4px 0",
                    }}
                  >
                    <FallOutlined
                      style={{ marginRight: 4, fontSize: 11 }}
                    />
                    <b>{s.supplierName}</b> has a low evaluation score (
                    {s.score.toFixed(1)}/100)
                  </div>
                ))}
              {partialCoverageSuppliers.map((sc) => (
                <div
                  key={sc.supplierID}
                  style={{
                    fontSize: 12,
                    color: "#8c4646",
                    padding: "4px 0",
                  }}
                >
                  <WarningOutlined
                    style={{ marginRight: 4, fontSize: 11 }}
                  />
                  <b>{sc.supplierName}</b> only quoted{" "}
                  {sc.quotedCount} of {totalItems} line items
                </div>
              ))}
            </div>
          )}

          {/* --- 9. Allocation Progress --- */}
          {shortlistedData.length > 0 && (
            <div style={sectionStyle}>
              <div style={sectionHeaderStyle}>
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
                Allocation Progress
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 12,
                  marginBottom: 4,
                }}
              >
                <span style={{ color: "#666" }}>
                  {shortlistedData.length} allocation(s) made
                </span>
                {hasAskedQty && (
                  <span style={{ color: "#666" }}>
                    {totalAwardedQty} / {totalAskedQty} units awarded
                  </span>
                )}
              </div>
              {hasAskedQty && totalAskedQty > 0 && (
                <Progress
                  percent={Math.round(
                    (totalAwardedQty / totalAskedQty) * 100
                  )}
                  strokeColor="#52c41a"
                  size="small"
                />
              )}
              <div style={{ marginTop: 8 }}>
                {Array.from(
                  new Set(shortlistedData.map((s) => s.supplierName))
                ).map((name) => {
                  const count = shortlistedData.filter(
                    (s) => s.supplierName === name
                  ).length;
                  return (
                    <Tag
                      key={name}
                      color="green"
                      style={{ fontSize: 11, marginBottom: 4 }}
                    >
                      {name}: {count} item(s)
                    </Tag>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

export default InsightsDrawer;
